import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { FilterIcon, SearchIcon, LayoutGridIcon, LayoutListIcon, PaletteIcon, BookIcon, UserIcon, CameraIcon, ShoppingBagIcon, PenToolIcon } from 'lucide-react';
import { useDarkMode } from '../context/DarkModeContext';
import { useDarkClasses } from '../hooks/useDarkClasses';
import { useRoleTheme } from '../hooks/useRoleTheme';
import { FilterSidebar, FilterSidebarWrapper, FilterSection } from '../components/UI/FilterSidebar';
import { SearchWithAutocomplete } from '../components/UI/SearchWithAutocomplete';
import GalleryView, { GalleryItem } from '../components/Creative/GalleryView';
// Mock Data for Gallery with mixed aspect ratios
const galleryItems: GalleryItem[] = [{
  id: 'g1',
  title: 'Dark Fantasy Cover',
  imageUrl: 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?w=600',
  creator: 'Alex Morgan',
  price: 500,
  priceType: 'starting',
  serviceType: 'Cover Design',
  genre: ['Fantasy', 'Dark Fantasy'],
  description: 'High fantasy cover art featuring typography and illustration.',
  aspectRatio: 'portrait'
}, {
  id: 'g2',
  title: 'Character Art - Rogue',
  imageUrl: 'https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=600',
  creator: 'Jordan Lee',
  price: 150,
  priceType: 'commissioned',
  serviceType: 'Character Art',
  genre: ['Fantasy', 'RPG'],
  description: 'Full body character commission for D&D or fantasy novels.',
  aspectRatio: 'portrait'
}, {
  id: 'g3',
  title: 'Sci-Fi Landscape',
  imageUrl: 'https://images.unsplash.com/photo-1618666012174-83b441c0bc76?w=600',
  creator: 'Alex Morgan',
  price: 450,
  priceType: 'starting',
  serviceType: 'Cover Design',
  genre: ['Sci-Fi', 'Space Opera'],
  description: 'Premade sci-fi landscape cover.',
  aspectRatio: 'landscape'
}, {
  id: 'g4',
  title: 'Cozy Mystery Bundle',
  imageUrl: 'https://images.unsplash.com/photo-1529374255404-311a2a4f1fd9?w=600',
  creator: 'Literary Apparel',
  price: 45,
  priceType: 'fixed',
  serviceType: 'Merch',
  genre: ['Mystery', 'Cozy Mystery'],
  description: 'T-shirt and tote bag bundle for cozy mystery lovers.',
  aspectRatio: 'square'
}, {
  id: 'g5',
  title: 'Fantasy Map - World',
  imageUrl: 'https://images.unsplash.com/photo-1524661135-423995f22d0b?w=600',
  creator: 'Map Makers Guild',
  price: 300,
  priceType: 'commissioned',
  serviceType: 'Cartography',
  genre: ['Fantasy', 'Epic Fantasy'],
  description: 'Detailed world map for fantasy novels.',
  aspectRatio: 'landscape'
}, {
  id: 'g6',
  title: 'Romance Couple Art',
  imageUrl: 'https://images.unsplash.com/photo-1516589178581-6cd7833ae3b2?w=600',
  creator: 'Jordan Lee',
  price: 200,
  priceType: 'commissioned',
  serviceType: 'Character Art',
  genre: ['Romance', 'Contemporary'],
  description: 'Couple portrait for romance covers or inserts.',
  aspectRatio: 'portrait'
}, {
  id: 'g7',
  title: 'Neon Cyberpunk City',
  imageUrl: 'https://images.unsplash.com/photo-1555680202-c86f0e12f086?w=600',
  creator: 'Pixel Forge',
  price: 350,
  priceType: 'starting',
  serviceType: 'Illustration',
  genre: ['Sci-Fi', 'Cyberpunk'],
  description: 'Detailed environmental concept art for sci-fi settings.',
  aspectRatio: 'landscape'
}, {
  id: 'g8',
  title: 'Vintage Horror Poster',
  imageUrl: 'https://images.unsplash.com/photo-1626544827763-d516dce335ca?w=600',
  creator: 'Grim Arts',
  price: 120,
  priceType: 'fixed',
  serviceType: 'Illustration',
  genre: ['Horror', 'Vintage'],
  description: 'Retro style horror poster design.',
  aspectRatio: 'portrait'
}];
// Mock Creator Preferences (In a real app, this would be joined from the user table)
const creatorPreferences: Record<string, {
  willWorkOn: string[];
  wontWorkOn: string[];
  maxSpice: number;
}> = {
  'Alex Morgan': {
    willWorkOn: ['Fantasy', 'Sci-Fi', 'Dark Fantasy'],
    wontWorkOn: ['Erotica'],
    maxSpice: 4
  },
  'Jordan Lee': {
    willWorkOn: ['Fantasy', 'Romance', 'Character Art'],
    wontWorkOn: [],
    maxSpice: 5
  },
  'Literary Apparel': {
    willWorkOn: ['Romance', 'Mystery', 'Cozy Mystery'],
    wontWorkOn: [],
    maxSpice: 5
  },
  'Map Makers Guild': {
    willWorkOn: ['Fantasy', 'Epic Fantasy'],
    wontWorkOn: [],
    maxSpice: 5
  },
  'Pixel Forge': {
    willWorkOn: ['Sci-Fi', 'Cyberpunk'],
    wontWorkOn: [],
    maxSpice: 3
  },
  'Grim Arts': {
    willWorkOn: ['Horror', 'Thriller'],
    wontWorkOn: ['Romance'],
    maxSpice: 5
  }
};
const GalleryHub = () => {
  const {
    darkMode
  } = useDarkMode();
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  // URL Params
  const typeParam = searchParams.get('type') || 'all';
  const queryParam = searchParams.get('q') || '';
  // State
  const [activeTab, setActiveTab] = useState(typeParam);
  const [searchQuery, setSearchQuery] = useState(queryParam);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  // Filter States
  const [selectedGenres, setSelectedGenres] = useState<string[]>([]);
  const [selectedSubGenres, setSelectedSubGenres] = useState<string[]>([]);
  const [selectedContentLevel, setSelectedContentLevel] = useState<string>('any');
  const [selectedPriceRange, setSelectedPriceRange] = useState<string>('all');
  const [selectedServiceTypes, setSelectedServiceTypes] = useState<string[]>([]);
  // Derived options
  const allServiceTypes = Array.from(new Set(galleryItems.map(i => i.serviceType).filter(Boolean))) as string[];
  // Sync state with URL
  useEffect(() => {
    if (typeParam && typeParam !== activeTab) {
      setActiveTab(typeParam);
    }
    if (queryParam && queryParam !== searchQuery) {
      setSearchQuery(queryParam);
    }
  }, [typeParam, queryParam]);
  const updateUrl = (type: string, query: string) => {
    const params: any = {};
    if (type && type !== 'all') params.type = type;
    if (query) params.q = query;
    setSearchParams(params);
  };
  const handleTabChange = (type: string) => {
    setActiveTab(type);
    updateUrl(type, searchQuery);
  };
  const handleSearch = (query: string) => {
    setSearchQuery(query);
    updateUrl(activeTab, query);
  };
  // Filter Logic
  const filteredItems = useMemo(() => {
    let result = [...galleryItems];
    // 1. Filter by Tab (Service Type Category)
    if (activeTab !== 'all') {
      if (activeTab === 'covers') result = result.filter(i => i.serviceType === 'Cover Design');else if (activeTab === 'interior') result = result.filter(i => i.serviceType === 'Cartography' || i.serviceType === 'Illustration');else if (activeTab === 'character') result = result.filter(i => i.serviceType === 'Character Art');else if (activeTab === 'merch') result = result.filter(i => i.serviceType === 'Merch');
    }
    // 2. Filter by Search
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(item => item.title.toLowerCase().includes(q) || item.creator.toLowerCase().includes(q) || item.description?.toLowerCase().includes(q) || item.genre?.some(g => g.toLowerCase().includes(q)));
    }
    // 3. Filter by Price Range
    if (selectedPriceRange !== 'all') {
      if (selectedPriceRange === 'under100') {
        result = result.filter(item => item.price < 100);
      } else if (selectedPriceRange === '100to250') {
        result = result.filter(item => item.price >= 100 && item.price <= 250);
      } else if (selectedPriceRange === 'over250') {
        result = result.filter(item => item.price > 250);
      }
    }
    // 4. Filter by Service Type
    if (selectedServiceTypes.length > 0) {
      result = result.filter(item => item.serviceType && selectedServiceTypes.includes(item.serviceType));
    }
    // 5. Content-Aware Filtering (Creator Preferences)
    result = result.filter(item => {
      const creatorPrefs = creatorPreferences[item.creator] || {
        willWorkOn: [],
        wontWorkOn: [],
        maxSpice: 5
      };
      // Sub-Genre Filter
      if (selectedSubGenres.length > 0) {
        const worksOn = creatorPrefs.willWorkOn.some(g => selectedSubGenres.includes(g));
        const declines = creatorPrefs.wontWorkOn.some(g => selectedSubGenres.includes(g));
        const itemHasGenre = item.genre?.some(g => selectedSubGenres.includes(g));
        if (!((worksOn || itemHasGenre) && !declines)) return false;
      }
      // Content Level Filter
      if (selectedContentLevel !== 'any') {
        const requiredLevel = parseInt(selectedContentLevel);
        if (creatorPrefs.maxSpice < requiredLevel) return false;
      }
      return true;
    });
    // 6. Genre Filter
    if (selectedGenres.length > 0) {
      result = result.filter(item => item.genre?.some(g => selectedGenres.includes(g)));
    }
    return result;
  }, [activeTab, searchQuery, selectedGenres, selectedSubGenres, selectedContentLevel, selectedPriceRange, selectedServiceTypes]);
  // Filter Configuration
  const filterSections: FilterSection[] = [{
    id: 'price',
    title: 'Price Range',
    type: 'radio',
    options: [{
      value: 'all',
      label: 'All Prices'
    }, {
      value: 'under100',
      label: 'Under $100'
    }, {
      value: '100to250',
      label: '$100 - $250'
    }, {
      value: 'over250',
      label: '$250+'
    }],
    value: selectedPriceRange,
    onChange: setSelectedPriceRange
  }, {
    id: 'serviceType',
    title: 'Service Type',
    type: 'checkbox',
    options: allServiceTypes.map(t => ({
      value: t,
      label: t
    })),
    value: selectedServiceTypes,
    onChange: val => setSelectedServiceTypes(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val])
  }, {
    id: 'genre',
    title: 'Genre',
    type: 'checkbox',
    options: [{
      value: 'Fantasy',
      label: 'Fantasy'
    }, {
      value: 'Romance',
      label: 'Romance'
    }, {
      value: 'Sci-Fi',
      label: 'Sci-Fi'
    }, {
      value: 'Mystery',
      label: 'Mystery'
    }],
    value: selectedGenres,
    onChange: val => setSelectedGenres(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val])
  }, {
    id: 'subgenre',
    title: 'Sub-Genre / Niche',
    type: 'checkbox',
    options: [{
      value: 'Dark Fantasy',
      label: 'Dark Fantasy'
    }, {
      value: 'Epic Fantasy',
      label: 'Epic Fantasy'
    }, {
      value: 'Cozy Mystery',
      label: 'Cozy Mystery'
    }, {
      value: 'Space Opera',
      label: 'Space Opera'
    }],
    value: selectedSubGenres,
    onChange: val => setSelectedSubGenres(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val])
  }, {
    id: 'content-level',
    title: 'Content Comfort (Spice)',
    type: 'radio',
    options: [{
      value: 'any',
      label: 'Any Level'
    }, {
      value: '1',
      label: 'Spice 1+ (Mild)'
    }, {
      value: '3',
      label: 'Spice 3+ (Steamy)'
    }, {
      value: '5',
      label: 'Spice 5 (Explicit)'
    }],
    value: selectedContentLevel,
    onChange: setSelectedContentLevel
  }];
  const handleResetFilters = () => {
    setSelectedGenres([]);
    setSelectedSubGenres([]);
    setSelectedContentLevel('any');
    setSelectedPriceRange('all');
    setSelectedServiceTypes([]);
    setSearchQuery('');
  };
  const tabs = [{
    id: 'all',
    label: 'All',
    icon: LayoutGridIcon
  }, {
    id: 'covers',
    label: 'Cover Design',
    icon: BookIcon
  }, {
    id: 'interior',
    label: 'Interior Art',
    icon: PaletteIcon
  }, {
    id: 'character',
    label: 'Character Art',
    icon: UserIcon
  }, {
    id: 'merch',
    label: 'Merch & Branding',
    icon: ShoppingBagIcon
  }, {
    id: 'photography',
    label: 'Photography',
    icon: CameraIcon
  }];
  return <div className={`min-h-screen ${dk.page}`} data-id="element-9192" data-mp="2942mea2ykfap">
      {/* Header */}
      <div className={`pt-8 pb-6 px-4 sm:px-6 lg:px-8 ${dk.surface} border-b ${dk.border}`} data-id="element-9193" data-mp="2e2nka2uqmmvz">
        <div className="max-w-7xl mx-auto" data-id="element-9194" data-mp="20bisfmxm2yw7">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8" data-id="element-9195" data-mp="20ah6gnf24rzp">
            <div data-id="element-9196" data-mp="24ahvt33p2nlj">
              <h1 className={`text-3xl font-bold mb-2 ${dk.textHeading}`} data-id="element-9197" data-mp="249ycpj2b5t5x">
                Gallery Hub
              </h1>
              <p className={`text-lg ${dk.textSecondary}`} data-id="element-9198" data-mp="1vbcszkl3o83k">
                Browse portfolios and visual work from talented creatives.
              </p>
            </div>
            <div className="w-full md:w-96" data-id="element-9199" data-mp="1hdxzo4rq7g6b">
              <SearchWithAutocomplete value={searchQuery} onChange={handleSearch} placeholder="Search styles, genres, or creators..." variant="pill" suggestions={[]} data-id="element-9200" data-mp="1lcoshzp5sbnm" />
            </div>
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide -mx-4 px-4 md:mx-0 md:px-0" data-id="element-9201" data-mp="2qzipkffkr3yf">
            {tabs.map(tab => {
            const isActive = activeTab === tab.id;
            const Icon = tab.icon;
            return <button key={tab.id} onClick={() => handleTabChange(tab.id)} className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap border ${isActive ? `${rt.bgDark} text-white border-transparent shadow-md` : `${dk.surface} ${dk.textSecondary} ${dk.border} hover:${dk.surfaceMuted}`}`} data-id="element-9202" data-mp="2c42ovrkho9t2">
                  <Icon className="h-4 w-4" data-id="element-9203" data-mp="1tdusdsbaohlu" />
                  {tab.label}
                </button>;
          })}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-9204" data-mp="1kc4ymftxx6kw">
        <div className="flex flex-col lg:flex-row gap-8" data-id="element-9205" data-mp="3vzqotkeim1zx">
          {/* Mobile Filter Toggle */}
          <div className="lg:hidden mb-4" data-id="element-9206" data-mp="3vxvlp6279at">
            <button onClick={() => setSidebarOpen(true)} className={`w-full flex items-center justify-center px-4 py-3 border rounded-lg shadow-sm ${dk.card} ${dk.textPrimary}`} data-id="element-9207" data-mp="3uwkxlsygotm">
              <FilterIcon className="h-5 w-5 mr-2" data-id="element-9208" data-mp="dtlt2mv1uuwo" />
              Filters
            </button>
          </div>

          {/* Sidebar */}
          <FilterSidebarWrapper isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} isCollapsed={sidebarCollapsed} onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} data-id="element-9209" data-mp="3hau7sxuhden6">
            <FilterSidebar title="Filters" sections={filterSections} onReset={handleResetFilters} searchable={false} data-id="element-9210" data-mp="3r1fcn30qzfej" />
          </FilterSidebarWrapper>

          {/* Gallery Grid */}
          <div className="flex-1 min-w-0" data-id="element-9211" data-mp="2qjn8jmi95poe">
            <GalleryView items={filteredItems} title="" categoryName={activeTab === 'all' ? 'Creative Work' : tabs.find(t => t.id === activeTab)?.label || 'Work'} data-id="element-9212" data-mp="2qic5jowmslyc" />
          </div>
        </div>
      </div>
    </div>;
};
export default GalleryHub;